/**
 * Component: TopPerformersSection
 * Author: Dine AI
 * Last Modified: December 17, 2025 - Light mode conversion
 */
import { motion } from 'framer-motion';
import { Sparkles, Calendar, Info, TrendingUp, Image } from 'lucide-react';
import { formatCurrency, formatNumber } from '@/utils/formatting';
import type { PopularItem } from '@/types/analytics';

interface TopPerformersSectionProps {
  items: PopularItem[];
  weekStarting: string | null;
  weekEnding: string | null;
  isHistorical?: boolean;
  dataNotice?: string;
  loading?: boolean;
}

const isDataStale = (weekEnding: string | null | undefined) => {
  if (!weekEnding) return false;
  
  const today = new Date();
  const dayOfWeek = today.getDay();
  
  // Calculate the most recent complete week's ending (Saturday)
  let mostRecentCompleteSaturday: Date;
  
  if (dayOfWeek === 0) {
    // Today is Sunday, most recent complete week ended yesterday (Saturday)
    mostRecentCompleteSaturday = new Date(today);
    mostRecentCompleteSaturday.setDate(today.getDate() - 1);
  } else if (dayOfWeek === 6) {
    // Today is Saturday, this week is not complete yet
    // Most recent complete week ended last Saturday (7 days ago)
    mostRecentCompleteSaturday = new Date(today);
    mostRecentCompleteSaturday.setDate(today.getDate() - 7);
  } else {
    // For Mon-Fri, go back to last Saturday
    const daysToLastSaturday = dayOfWeek + 1;
    mostRecentCompleteSaturday = new Date(today);
    mostRecentCompleteSaturday.setDate(today.getDate() - daysToLastSaturday);
  }
  
  // Parse the date string properly to avoid timezone issues
  // weekEnding comes as "YYYY-MM-DD" from the backend
  // Create date at noon to avoid timezone shifts
  const dataWeekEndDate = new Date(weekEnding + 'T12:00:00');
  
  // Format dates as YYYY-MM-DD for comparison
  const dataYear = dataWeekEndDate.getFullYear();
  const dataMonth = String(dataWeekEndDate.getMonth() + 1).padStart(2, '0');
  const dataDay = String(dataWeekEndDate.getDate()).padStart(2, '0');
  const dataEndStr = `${dataYear}-${dataMonth}-${dataDay}`;
  
  const expectedYear = mostRecentCompleteSaturday.getFullYear();
  const expectedMonth = String(mostRecentCompleteSaturday.getMonth() + 1).padStart(2, '0');
  const expectedDay = String(mostRecentCompleteSaturday.getDate()).padStart(2, '0');
  const expectedEndStr = `${expectedYear}-${expectedMonth}-${expectedDay}`;
  
  // Data is stale if it's NOT from the most recent complete week
  // This shows users when they're not seeing the latest available data
  return dataEndStr !== expectedEndStr;
};

export function TopPerformersSection({
  items,
  weekStarting,
  weekEnding,
  isHistorical,
  dataNotice,
  loading = false
}: TopPerformersSectionProps) {
  
  if (loading) {
    return (
      <div className="mb-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-100 rounded-xl h-96"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show nothing if no items and no notice
  if (!items?.length && !dataNotice) {
    return null;
  }

  const topThreeRevenue = items.slice(0, 3).reduce((sum, item) => sum + (item.revenue || 0), 0);

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <motion.div
          className="p-3 bg-[#FDB913]/10 rounded-xl relative"
          whileHover={{ scale: 1.05 }}
        >
          <TrendingUp className="h-6 w-6 text-[#FDB913]" />
          <motion.div
            className="absolute -top-1 -right-1"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <Sparkles className="h-4 w-4 text-[#FDB913]" />
          </motion.div>
        </motion.div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-gray-900">
            This Week's Champions
          </h3>
          <p className="text-gray-600">
            {formatCurrency(topThreeRevenue)} in revenue from top 3 • {
              weekStarting && weekEnding ? (
                `Week of ${new Date(weekStarting + 'T12:00:00').toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })} - ${new Date(weekEnding + 'T12:00:00').toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}`
              ) : (
                'Last 7 days'
              )
            }
          </p>
        </div>

        {/* Date Range Indicator */}
        <div className="ml-auto">
          <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg">
            <Calendar className="w-4 h-4" />
            <span className="flex items-center gap-2">
              {weekStarting && weekEnding ? (
                <>
                  <span className="text-gray-400">Last Refresh:</span>{' '}
                  <span>
                    {new Date(weekStarting + 'T12:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })} - {new Date(weekEnding + 'T12:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                  {isDataStale(weekEnding) && (
                    <div className="relative group">
                      <Info className="w-4 h-4 text-red-500 cursor-help" />
                      <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-64">
                        <div>
                          Showing data from {new Date(weekStarting + 'T12:00:00').toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}-{new Date(weekEnding + 'T12:00:00').toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}, {new Date(weekEnding).getFullYear()}.
                          <br />
                          We are aware of the issue and are actively working to resolve this asap.
                        </div>
                        <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                'Last 7 days'
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Data Notice */}
      {dataNotice && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">{dataNotice}</p>
        </div>
      )}

      {/* Top Performers Cards */}
      {items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.slice(0, 3).map((item, index) => {
            const medals = ["🥇", "🥈", "🥉"];
            const medalColors = [
              "from-yellow-400 to-amber-500",
              "from-gray-300 to-gray-400",
              "from-orange-600 to-orange-700",
            ];

            return (
              <motion.div
                key={index}
                className="relative group"
                whileHover={{
                  y: -5,
                  transition: { duration: 0.2 },
                }}
              >
                {/* Medal Badge */}
                <div
                  className={`absolute -top-3 -right-3 z-10 w-16 h-16 bg-gradient-to-br ${medalColors[index]} rounded-full flex items-center justify-center shadow-lg`}
                >
                  <span className="text-3xl">{medals[index]}</span>
                </div>

                <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-gray-300 transition-all h-full flex flex-col shadow-sm">
                  {/* Item Image */}
                  {item.image_url ? (
                    <div className="h-48 overflow-hidden flex-shrink-0">
                      <img
                        src={`${item.image_url}${item.image_url.includes('?') ? '&' : '?'}cb=${Date.now()}`}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                      <Image className="w-16 h-16 text-gray-400" />
                    </div>
                  )}

                  {/* Item Details */}
                  <div className="p-4 flex flex-col flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-gray-900 text-lg flex-1">
                        {item.name}
                      </h4>
                      <span className="text-2xl font-bold text-green-600">
                        ${item.price}
                      </span>
                    </div>

                    <div className="flex-1 mb-3">
                      <p className="text-gray-500 text-sm">
                        {item.category}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-gray-500">Orders</p>
                        <p className="text-gray-900 font-semibold">
                          {formatNumber(item.orders)}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-gray-500">Revenue</p>
                        <p className="text-green-600 font-semibold">
                          {formatCurrency(item.revenue)}
                        </p>
                      </div>
                    </div>

                    {/* Optimization Opportunity */}
                    {item.optimization_opportunity && (
                      <div className="mt-3 text-sm text-[#b8860b] bg-[#FDB913]/10 rounded-lg p-2">
                        {item.optimization_opportunity}
                      </div>
                    )}

                    {/* Profit Margin */}
                    {item.profit_margin !== undefined && (
                      <div className="mt-3 flex items-center gap-1 text-green-600 text-sm">
                        <TrendingUp className="w-4 h-4" />
                        <span>{item.profit_margin}% profit margin</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}